import { useCallback, useEffect, useRef, useState } from 'react';
import { Coffee, Pause, Play, RotateCcw } from 'lucide-react';

const FOCUS_DURATION_MS = 25 * 60 * 1000;
const BREAK_DURATION_MS = 5 * 60 * 1000;

const MODES = {
  focus: {
    label: 'Focus',
    duration: FOCUS_DURATION_MS,
    icon: Play,
    accent: 'text-emerald-600',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    progress: 'bg-emerald-500',
  },
  break: {
    label: 'Break',
    duration: BREAK_DURATION_MS,
    icon: Coffee,
    accent: 'text-sky-600',
    badge: 'bg-sky-50 text-sky-700 border-sky-100',
    progress: 'bg-sky-500',
  },
};

const formatTime = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const PomodoroTimer = () => {
  const [mode, setMode] = useState('focus');
  const [remainingMs, setRemainingMs] = useState(FOCUS_DURATION_MS);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef({
    mode: 'focus',
    deadline: null,
    pausedRemainingMs: FOCUS_DURATION_MS,
  });

  const syncTimer = useCallback(() => {
    const timer = timerRef.current;

    if (!timer.deadline) {
      setMode(timer.mode);
      setRemainingMs(timer.pausedRemainingMs);
      return;
    }

    const now = Date.now();

    // Advance from the original deadline rather than from "now". This keeps
    // phase changes correct if the browser throttles or pauses the interval.
    while (now >= timer.deadline) {
      timer.mode = timer.mode === 'focus' ? 'break' : 'focus';
      timer.deadline += MODES[timer.mode].duration;
    }

    const nextRemainingMs = Math.max(0, timer.deadline - now);
    setMode(timer.mode);
    setRemainingMs(nextRemainingMs);
  }, []);

  useEffect(() => {
    if (!isRunning) return undefined;

    syncTimer();
    const intervalId = window.setInterval(syncTimer, 250);
    document.addEventListener('visibilitychange', syncTimer);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', syncTimer);
    };
  }, [isRunning, syncTimer]);

  const handleStartStop = () => {
    const timer = timerRef.current;

    if (isRunning) {
      syncTimer();
      timer.pausedRemainingMs = Math.max(0, timer.deadline - Date.now());
      timer.deadline = null;
      setRemainingMs(timer.pausedRemainingMs);
      setIsRunning(false);
      return;
    }

    timer.deadline = Date.now() + timer.pausedRemainingMs;
    setIsRunning(true);
  };

  const handleReset = () => {
    timerRef.current = {
      mode: 'focus',
      deadline: null,
      pausedRemainingMs: FOCUS_DURATION_MS,
    };
    setMode('focus');
    setRemainingMs(FOCUS_DURATION_MS);
    setIsRunning(false);
  };

  const currentMode = MODES[mode];
  const ModeIcon = currentMode.icon;
  const progress = Math.min(
    100,
    Math.max(0, ((currentMode.duration - remainingMs) / currentMode.duration) * 100)
  );

  return (
    <section className="w-full max-w-sm rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm sm:p-5" aria-label="Pomodoro timer">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Pomodoro</h2>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${currentMode.badge}`}>
          <ModeIcon size={12} aria-hidden="true" />
          {currentMode.label}
        </span>
      </div>

      <div className="py-6 text-center sm:py-8">
        <p className={`font-mono text-5xl font-bold tracking-tight tabular-nums sm:text-6xl ${currentMode.accent}`} aria-live="polite">
          {formatTime(remainingMs)}
        </p>
        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
          {isRunning ? 'Timer running' : 'Ready when you are'}
        </p>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-100" aria-hidden="true">
        <div className={`h-full rounded-full transition-[width] duration-200 ${currentMode.progress}`} style={{ width: `${progress}%` }} />
      </div>

      <div className="mt-5 flex items-center gap-2">
        <button
          type="button"
          onClick={handleStartStop}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-black uppercase tracking-wider text-white transition-all active:scale-[0.98] ${mode === 'focus' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-sky-600 hover:bg-sky-700'}`}
        >
          {isRunning ? <Pause size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
          {isRunning ? 'Stop' : 'Start'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center justify-center rounded-xl border border-zinc-200 p-3 text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-900 active:scale-[0.98]"
          aria-label="Reset Pomodoro timer to 25 minutes of focus"
          title="Reset timer"
        >
          <RotateCcw size={16} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

export default PomodoroTimer;
